// How We Work section content - SEO optimized
export const HOW_WE_WORK_CONTENT = {
  hero: {
    title: 'How We Work',
    subtitle: 'A smarter approach to home maintenance that saves you time, money, and stress'
  },

  valueProposition: {
    title: 'Affordable Home Maintenance Through Smart Partnerships',
    description: 'Our subscription-based model allows us to negotiate lower prices with trusted service providers. Because they can count on reliable, recurring work from our subscriber base, they pass those savings directly to you.',
    highlights: [
      {
        stat: 'Up to 50%',
        label: 'Cost reduction through preventative maintenance vs. reactive repairs*'
      },
      {
        stat: '1',
        label: 'Phone number for all your home service needs'
      },
      {
        stat: '100%',
        label: 'Vetted and trusted service providers'
      }
    ]
  },

  problemSolution: {
    title: 'The Traditional Way vs. The HSP Way',
    traditional: {
      title: 'Traditional Home Maintenance',
      subtitle: 'Expensive, Reactive, Stressful',
      problems: [
        'Wait until something breaks, then pay emergency rates',
        'Search for and vet multiple contractors for different services',
        'Manage multiple phone numbers, schedules, and invoices',
        'Pay premium prices for one-time service calls',
        'No relationship with providers - inconsistent quality',
        'Unexpected costs strain your budget'
      ]
    },
    hspWay: {
      title: 'The HSP Subscription Model',
      subtitle: 'Affordable, Preventative, Simple',
      solutions: [
        'Prevent problems before they start with regular maintenance',
        'One trusted company handles all your exterior home services',
        'Single phone number, one bill, coordinated scheduling',
        'Negotiated subscription rates save you money',
        'Providers build lasting relationships with your home',
        'Predictable monthly costs - no surprises'
      ]
    }
  },

  howItWorks: {
    title: 'Home Maintenance Plans, Simplified',
    subtitle: 'From signup to service, we make home care effortless',
    steps: [
      {
        number: '1',
        title: 'Choose Your Services, or Plan',
        description: 'Select a package of services that matches your home\'s needs. Basic, Standard, or Premium - all include core services with transparent monthly pricing.',
        icon: 'select'
      },
      {
        number: '2',
        title: 'We Schedule Everything',
        description: 'Our team coordinates with our trusted service providers to schedule regular maintenance visits. You get reminders, but we handle all the logistics.',
        icon: 'calendar'
      },
      {
        number: '3',
        title: 'Service Providers Come to You',
        description: 'Vetted, professional service providers arrive on schedule. They know your property and build a relationship over time, ensuring consistent, quality work.',
        icon: 'service'
      },
      {
        number: '4',
        title: 'Enjoy Peace of Mind',
        description: 'Your home stays in top condition year-round. Need something extra? One call to us, and we coordinate it. No searching, no vetting, no stress.',
        icon: 'peace'
      }
    ]
  },

  customerBenefits: {
    title: 'How We Help Each Customer Group',
    subtitle: 'Tailored solutions for your unique home maintenance challenges',
    groups: [
      {
        type: 'Busy Homeowners & Working Professionals',
        tagline: 'Get Your Weekends Back',
        mainBenefit: 'Stop sacrificing family time and relaxation to yard work and home maintenance',
        benefits: [
          {
            title: 'Time Savings',
            description: 'Reclaim 8-12 hours per month spent on home maintenance tasks'
          },
          {
            title: 'No Project Management',
            description: 'We coordinate all services, schedules, and providers - you do nothing'
          },
          {
            title: 'Predictable Costs',
            description: 'One fixed monthly bill replaces multiple unpredictable service calls'
          },
          {
            title: 'Professional Results',
            description: 'Consistent, high-quality work from trained professionals, not DIY stress'
          }
        ],
        savings: 'Save 10-15 hours per month and avoid costly emergency repairs'
      },
      {
        type: 'Property Investors & Landlords',
        tagline: 'Maximize ROI Through Preventative Care',
        mainBenefit: 'Reduce repair costs by up to 50% while maintaining property value and tenant satisfaction',
        citation: 'According to the International Facility Management Association (IFMA), preventative maintenance can reduce overall maintenance costs by 12-18% and significantly extend asset life',
        benefits: [
          {
            title: 'Portfolio Management',
            description: 'One partner for all your properties - centralized scheduling and billing'
          },
          {
            title: 'Proven Cost Reduction',
            description: 'Preventative maintenance reduces major repair costs by up to 50% according to industry studies'
          },
          {
            title: 'Tenant Satisfaction',
            description: 'Well-maintained properties keep tenants happy and reduce turnover'
          },
          {
            title: 'Property Value Protection',
            description: 'Regular care preserves and increases long-term property value'
          }
        ],
        savings: 'Average 30-50% reduction in repair costs + increased property values (IFMA)'
      },
      {
        type: 'Senior Homeowners',
        tagline: 'Stay in Your Home Safely and Comfortably',
        mainBenefit: 'Enjoy your home without physical strain, safety risks, or maintenance worries',
        benefits: [
          {
            title: 'Safety First',
            description: 'No more climbing ladders, lifting heavy equipment, or physical strain'
          },
          {
            title: 'Trusted Relationships',
            description: 'Same providers visit regularly - they become familiar faces you can trust'
          },
          {
            title: 'Complete Care',
            description: 'All exterior maintenance handled - gutters, windows, lawn, cleaning'
          },
          {
            title: 'Simple Communication',
            description: 'One phone number for everything - no juggling multiple contractors'
          }
        ],
        savings: 'Stay independent in your home with affordable, reliable care'
      }
    ]
  },

  whyItWorks: {
    title: 'Why Our Model Works Better',
    subtitle: 'The power of subscription-based home services',
    reasons: [
      {
        title: 'Volume Creates Savings',
        description: 'Our subscription base provides consistent work for service providers. This allows them to offer lower rates while maintaining quality service.',
        benefit: 'You save 30-40% compared to one-time service calls'
      },
      {
        title: 'Prevention Beats Reaction',
        description: 'Regular maintenance catches small issues before they become expensive emergencies. Clogged gutters, overgrown landscaping, dirty windows - all prevented.',
        benefit: 'Avoid costly emergency repairs and extend the life of your home'
      },
      {
        title: 'Relationships Drive Quality',
        description: 'When providers service your home regularly, they understand its unique needs. They take pride in their work and are accountable for long-term results.',
        benefit: 'Better service quality and consistent results'
      },
      {
        title: 'Simplicity Reduces Stress',
        description: 'One company, one phone number, one bill. No more searching for contractors, comparing quotes, or managing multiple relationships.',
        benefit: 'Save hours of time and eliminate the stress of home maintenance'
      }
    ]
  },

  cta: {
    title: 'Ready to Simplify Your Home Maintenance?',
    subtitle: 'Join thousands of homeowners who have discovered the better way',
    buttonText: 'Get Your Free Quote',
    buttonLink: '/quote',
    secondaryText: 'Questions? Call us at (888) 504-6944'
  },

  sources: {
    title: 'Industry Research & Sources',
    citations: [
      {
        claim: 'Preventative maintenance cost savings',
        source: 'International Facility Management Association (IFMA)',
        detail: 'IFMA research indicates that preventative maintenance can reduce overall maintenance costs by 12-18% and prevent costly emergency repairs. Organizations with proactive maintenance programs report significant reductions in unexpected failures.',
        link: 'https://www.ifma.org'
      },
      {
        claim: 'Home maintenance ROI',
        source: 'National Association of Home Builders (NAHB)',
        detail: 'NAHB studies show that regular home maintenance preserves property value and prevents 30-50% of potential repair costs by addressing issues early before they escalate into major problems.',
        link: 'https://www.nahb.org'
      },
      {
        claim: 'Emergency vs. preventative maintenance costs',
        source: 'U.S. Department of Energy',
        detail: 'DOE research demonstrates that preventative maintenance programs reduce equipment failures by 70% and lower maintenance costs by 25-30% compared to reactive maintenance approaches.',
        link: 'https://www.energy.gov'
      },
      {
        claim: 'Property preservation through maintenance',
        source: 'Insurance Institute for Business & Home Safety (IBHS)',
        detail: 'IBHS data shows that regular maintenance of key home systems (gutters, roofing, drainage) significantly reduces insurance claims and prevents water damage that can cost tens of thousands to repair.',
        link: 'https://www.ibhs.org'
      }
    ],
    disclaimer: '*Cost savings vary based on property size, condition, and maintenance history. Figures represent industry averages from reputable sources. Individual results may differ.'
  }
};
